import { zodResolver } from '@hookform/resolvers/zod';
import { processoSchema, PROCESSO_PADRAO, type Processo } from '@plane/shared';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import { Field } from '@/components/field';
import { useProcesso, useSaveProcesso } from '@/modules/site-content/hooks/use-conteudo';
import { ApiRequestError } from '@/services/http-client';

function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiRequestError) return erro.message;
  if (erro instanceof Error) return erro.message;

  return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

export function ProcessoPage() {
  const consulta = useProcesso();
  const salvar = useSaveProcesso();
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  if (consulta.isPending) return <p className="text-sm text-ink-soft">Carregando…</p>;

  if (consulta.isError) {
    return (
      <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
        {mensagemDeErro(consulta.error)}
      </p>
    );
  }

  return (
    <Formulario
      key={consulta.data ? 'salvo' : 'padrao'}
      inicial={consulta.data ?? PROCESSO_PADRAO}
      inedito={consulta.data === null}
      erro={erro}
      salvo={salvo}
      salvando={salvar.isPending}
      aoEnviar={(processo) => {
        setErro(null);
        setSalvo(false);

        salvar
          .mutateAsync(processo)
          .then(() => setSalvo(true))
          .catch((falha: unknown) => setErro(mensagemDeErro(falha)));
      }}
    />
  );
}

function Formulario({
  inicial,
  inedito,
  erro,
  salvo,
  salvando,
  aoEnviar,
}: {
  inicial: Processo;
  inedito: boolean;
  erro: string | null;
  salvo: boolean;
  salvando: boolean;
  aoEnviar: (processo: Processo) => void;
}) {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Processo>({
    resolver: zodResolver(processoSchema),
    defaultValues: inicial,
  });

  const etapas = useFieldArray({ control, name: 'etapas' });

  return (
    <form
      onSubmit={(evento) => {
        void handleSubmit(aoEnviar)(evento);
      }}
      className="flex max-w-2xl flex-col gap-6"
    >
      <div>
        <p className="text-xs tracking-[0.18em] text-brass uppercase">Conteúdo do site</p>
        <h1 className="mt-2 font-display text-2xl">Processo</h1>
        <p className="mt-3 text-sm text-ink-soft">
          As etapas do atendimento, na quarta seção do site.
        </p>
      </div>

      {inedito && (
        <p className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          Esta seção ainda não foi salva. Os campos abaixo estão com o conteúdo do protótipo —{' '}
          <strong>confira os prazos</strong>, porque prazo publicado vira expectativa de quem
          contrata.
        </p>
      )}

      <Field
        id="kicker"
        label="Etiqueta acima do título"
        placeholder="04 — Processo"
        error={errors.kicker?.message}
        {...register('kicker')}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="titulo" className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Título
        </label>

        <textarea
          id="titulo"
          rows={2}
          aria-invalid={errors.titulo ? true : undefined}
          aria-describedby="titulo-ajuda"
          className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none aria-invalid:border-brass"
          {...register('titulo')}
        />

        <p id="titulo-ajuda" className="text-xs text-ink-soft">
          Quebre a linha onde quiser que o título quebre. Para destacar uma palavra em verde, ponha
          entre asteriscos: <code>Nenhuma *terceirizada*.</code>
        </p>

        {errors.titulo && (
          <p role="alert" className="text-xs text-brass">
            {errors.titulo.message}
          </p>
        )}
      </div>

      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">Etapas</legend>

        <p className="mt-1.5 text-xs text-ink-soft">
          A numeração (01, 02…) sai da ordem da lista — não é campo, e por isso nunca contradiz a
          ordem.
        </p>

        <ol className="mt-4 flex list-none flex-col gap-6 p-0">
          {etapas.fields.map((campo, indice) => (
            <li key={campo.id} className="flex flex-col gap-4 border-l-2 border-sand pl-4">
              <div className="flex items-center justify-between">
                <span className="font-display text-lg text-brand-600 tabular-nums">
                  {String(indice + 1).padStart(2, '0')}
                </span>

                <div className="flex items-center gap-3 text-xs">
                  <button
                    type="button"
                    onClick={() => etapas.move(indice, indice - 1)}
                    disabled={indice === 0}
                    aria-label={`Subir a etapa ${String(indice + 1)}`}
                    className="text-brand-700 underline disabled:text-ink-soft/40 disabled:no-underline"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => etapas.move(indice, indice + 1)}
                    disabled={indice === etapas.fields.length - 1}
                    aria-label={`Descer a etapa ${String(indice + 1)}`}
                    className="text-brand-700 underline disabled:text-ink-soft/40 disabled:no-underline"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => etapas.remove(indice)}
                    disabled={etapas.fields.length === 1}
                    className="text-brass underline disabled:text-ink-soft/40 disabled:no-underline"
                  >
                    Remover
                  </button>
                </div>
              </div>

              <Field
                id={`etapa-titulo-${String(indice)}`}
                label="Título da etapa"
                error={errors.etapas?.[indice]?.titulo?.message}
                {...register(`etapas.${indice}.titulo`)}
              />

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor={`etapa-descricao-${String(indice)}`}
                  className="text-xs tracking-[0.12em] text-ink-soft uppercase"
                >
                  Descrição
                </label>
                <textarea
                  id={`etapa-descricao-${String(indice)}`}
                  rows={3}
                  aria-invalid={errors.etapas?.[indice]?.descricao ? true : undefined}
                  className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none aria-invalid:border-brass"
                  {...register(`etapas.${indice}.descricao`)}
                />
                {errors.etapas?.[indice]?.descricao && (
                  <p role="alert" className="text-xs text-brass">
                    {errors.etapas[indice].descricao.message}
                  </p>
                )}
              </div>

              <Field
                id={`etapa-prazo-${String(indice)}`}
                label="Prazo (opcional)"
                placeholder="20 a 25 dias"
                error={errors.etapas?.[indice]?.prazo?.message}
                {...register(`etapas.${indice}.prazo`)}
              />
            </li>
          ))}
        </ol>

        {etapas.fields.length < 8 && (
          <button
            type="button"
            onClick={() => etapas.append({ titulo: '', descricao: '', prazo: '' })}
            className="mt-4 text-sm text-brand-700 underline"
          >
            Adicionar etapa
          </button>
        )}
      </fieldset>

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          {erro}
        </p>
      )}

      {salvo && (
        <p role="status" className="border-l-2 border-brand-600 pl-3 text-sm text-brand-700">
          Salvo. O site já mostra o conteúdo novo.
        </p>
      )}

      <button
        type="submit"
        disabled={salvando}
        className="self-start bg-brand-600 px-5 py-2.5 text-sm text-white transition-opacity disabled:opacity-50"
      >
        {salvando ? 'Salvando…' : 'Salvar'}
      </button>
    </form>
  );
}
